import {
  ExtendedDefaultOrderGridDataPos,
  GetDeliverySplittingPartsCalculationRes as CalculatedSplittingParts,
} from '../../../../../../../../customer/services/customer.service.ts';
import { IDefaultOrderGridData } from '../../../../defaultOrder.state.ts';
import { IPreparedPositions } from './cells/customer.cell.tsx';
import { Pub, Sub, State } from '../../../../../../../../../shared/state/state.abstract.ts';
import { BehaviorSubject, share, Observable, merge, tap, map, filter, switchMap } from 'rxjs';
import { Wa_DefaultOrderPositionSplittingPresetInput as PositionSplittingPreset } from '../../../../../../../../../graphql/generatedModel.ts';
import { responseHandler } from '../../../../../../../../../shared/responseHandler/responseHandler.ts';
import { articleService } from '../../../../../../../services/article.service.ts';

const initDeliverySplittingState: IDeliverySplittingState = {
  action: undefined,
  selectedPos: null,
  deliverySplittingGridData: [],
  preparedPositions: {},
  calculatedSplitting: {},
};

class PubImpl extends Pub<IDeliverySplittingState> {
  initGridData(deliverySplittingGridData: IDeliverySplittingState['deliverySplittingGridData']) {
    this.emit('init', { deliverySplittingGridData });
  }
  selectPosition(selectedPos: IDeliverySplittingState['selectedPos']) {
    this.emit('selectPosition', { selectedPos });
  }
  updateGridData(deliverySplittingGridData: IDeliverySplittingState['deliverySplittingGridData']) {
    this.emit('updateGridData', { deliverySplittingGridData });
  }
  clearCalculatedSplitting() {
    this.emit('clearCalculatedSplitting', { calculatedSplitting: {} });
  }
  calculatePositionSplitting(
    selectedPos: IDeliverySplittingState['selectedPos'],
    preparedPositions: IDeliverySplittingState['preparedPositions'],
  ) {
    this.emit('calculatePositionSplitting', { selectedPos, preparedPositions });
  }
}

class SubImpl extends Sub<IDeliverySplittingState> {
  private customerCellLoading$ = new BehaviorSubject<number | null>(null);
  private shareCustomerCellLoading$ = this.customerCellLoading$.pipe(share());
  protected actionHandlers(): Observable<IDeliverySplittingState> {
    return merge(this.calculatePositionSplitting(), this.updateState()).pipe(
      tap((state) => {
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }
  customerCellLoading(): Observable<number | null> {
    return this.shareCustomerCellLoading$;
  }

  private calculatePositionSplitting(): Observable<IDeliverySplittingState> {
    return this.actionListener('calculatePositionSplitting').pipe(
      filter(({ selectedPos }) => !(this.customerCellLoading$.value === selectedPos!.id)),
      tap((state) => {
        const { preparedPositions, selectedPos } = state;
        const { id } = selectedPos!;
        this.customerCellLoading$.next(id);
        this.stream$.next({
          ...state,
          action: 'updateState',
        });
        articleService.pub.getDeliverySplittingPartsCalculation({
          position: preparedPositions as PositionSplittingPreset,
          id,
        });
      }),
      switchMap(() => {
        return articleService.sub.getDeliverySplittingPartsCalculation().pipe(
          responseHandler<CalculatedSplittingParts>({
            errorReturnType: {
              id: 0,
              mondayParts: [],
              tuesdayParts: [],
              wednesdayParts: [],
              thursdayParts: [],
              fridayParts: [],
              saturdayParts: [],
              sundayParts: [],
            },
          }),
        );
      }),
      map((data) => {
        const updatedState = structuredClone(this.stream$.getValue());
        const { selectedPos } = updatedState;
        const { id, ...rest } = data;
        this.customerCellLoading$.next(null);
        if (Object.keys(rest).length && selectedPos?.id === id) {
          updatedState.calculatedSplitting = data;
        }
        return updatedState;
      }),
    );
  }

  private updateState(): Observable<IDeliverySplittingState> {
    return this.actionListener([
      'updateGridData',
      'selectPosition',
      'clearCalculatedSplitting',
      'updateState',
    ]).pipe(
      map((state) => {
        const updatedState = structuredClone(state);
        if (updatedState.action === 'selectPosition' || updatedState.action === 'updateGridData') {
          updatedState.calculatedSplitting = {};
        }
        return updatedState;
      }),
    );
  }
}

class DeliverySplittingState extends State<IDeliverySplittingState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const deliverySplittingState = new DeliverySplittingState(initDeliverySplittingState);

export interface IDeliverySplittingState {
  action:
    | 'init'
    | 'updateGridData'
    | 'selectPosition'
    | 'calculatePositionSplitting'
    | 'clearCalculatedSplitting'
    | 'updateState'
    | 'internalUpdate'
    | undefined;
  selectedPos: ExtendedDefaultOrderGridDataPos | null;
  deliverySplittingGridData: IDefaultOrderGridData;
  preparedPositions: IPreparedPositions;
  calculatedSplitting: Partial<CalculatedSplittingParts>;
}
